/**
 * Role Constants and Utility Functions for RBAC
 * 
 * This file contains all role-related constants and helper functions
 * for role-based access control throughout the application.
 * 
 * Role Hierarchy:
 * - Super Admin: Full platform access, can manage users and access account settings
 * - Admin: Company-level management, can create/edit/delete data
 * - Data Contributor: Can edit existing data
 * - Viewer: Read-only access
 */

export const ROLES = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  DATA_CONTRIBUTOR: 'Data Contributor',
  VIEWER: 'Viewer',
} as const;

export type RoleType = typeof ROLES[keyof typeof ROLES];

/**
 * Check if the role is Super Admin
 */
export const isSuperAdmin = (role?: string): boolean => role === ROLES.SUPER_ADMIN;

/**
 * Check if the role is Admin
 */
export const isAdmin = (role?: string): boolean => role === ROLES.ADMIN;

/**
 * Check if the role is Data Contributor
 */
export const isDataContributor = (role?: string): boolean => role === ROLES.DATA_CONTRIBUTOR;

/**
 * Check if the role is Viewer
 */
export const isViewer = (role?: string): boolean => role === ROLES.VIEWER;

/**
 * Check if user can create new data
 * Super Admin and Admin can create
 */
export const canCreate = (role?: string): boolean => {
  return isSuperAdmin(role) || isAdmin(role);
};

/**
 * Check if user can edit existing data
 * Super Admin, Admin, and Data Contributor can edit
 */
export const canEdit = (role?: string): boolean => {
  return isSuperAdmin(role) || isAdmin(role) || isDataContributor(role);
};

/**
 * Check if user can delete data
 * Super Admin and Admin can delete
 */
export const canDelete = (role?: string): boolean => {
  return isSuperAdmin(role) || isAdmin(role);
};

/**
 * Check if user can manage users (add/edit/delete users)
 * Only Super Admin can manage users
 */
export const canManageUsers = (role?: string): boolean => {
  return isSuperAdmin(role);
};

/**
 * Check if user can view user list
 * Super Admin and Admin can view users
 */
export const canViewUsers = (role?: string): boolean => {
  return isSuperAdmin(role) || isAdmin(role);
};

/**
 * Check if user can access account settings
 * Only Super Admin can access account settings
 */
export const canAccessAccount = (role?: string): boolean => {
  return isSuperAdmin(role);
};

/**
 * Check if user has admin-level access (Super Admin or Admin)
 */
export const hasAdminAccess = (role?: string): boolean => {
  return isSuperAdmin(role) || isAdmin(role);
};

/**
 * Permission matrix for all roles
 */
export const PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: {
    canManageUsers: true,
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canAccessAccount: true,
    canViewUsers: true,
  },
  [ROLES.ADMIN]: {
    canManageUsers: false,
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canAccessAccount: false,
    canViewUsers: true,
  },
  [ROLES.DATA_CONTRIBUTOR]: {
    canManageUsers: false,
    canCreate: false,
    canEdit: true,
    canDelete: false,
    canAccessAccount: false,
    canViewUsers: false,
  },
  [ROLES.VIEWER]: {
    canManageUsers: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canAccessAccount: false,
    canViewUsers: false,
  },
} as const;

/**
 * Get permissions for a specific role
 */
export const getPermissions = (role?: string) => {
  if (!role) {
    return PERMISSIONS[ROLES.VIEWER]; // Default to most restrictive
  }
  return PERMISSIONS[role as RoleType] || PERMISSIONS[ROLES.VIEWER];
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = (role?: string, permission?: keyof typeof PERMISSIONS[typeof ROLES.SUPER_ADMIN]): boolean => {
  if (!role || !permission) return false;
  const rolePermissions = getPermissions(role);
  return rolePermissions[permission] ?? false;
};

/**
 * Standard access denied messages for consistency across the app
 */
export const ACCESS_DENIED_MESSAGES = {
  GENERIC: "You don't have permission to perform this action",
  CREATE: "You don't have permission to create new items",
  EDIT: "You don't have permission to edit this item",
  DELETE: "You don't have permission to delete this item",
  ADMIN_ONLY: "This action requires admin access",
} as const;

/**
 * Type for access denied message keys
 */
export type AccessDeniedMessageKey = keyof typeof ACCESS_DENIED_MESSAGES;
